import axios from 'axios';
import * as z from 'zod';
import { store } from '../index';

import axiosRetry from 'axios-retry';
import { IMAGE_TYPES as IMAGE_TYPE } from '../common/PicabooConstants';
import { dotNetApiBase, phpApiBase } from '../config';
import { splitProduct } from '../helpers/ProductHelper';
import invariant from 'tiny-invariant';
import { deobfuscateProjectId } from './projectId';
import { showLoginModal } from '../ducks/modals';
import { setSaving } from '../ducks/changesets';

export const app2Service = () => {
  const service = axios.create({
    baseURL: phpApiBase,
    withCredentials: true,
  });

  service.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      return handleErrorResponse(error);
    },
  );

  return service;
};

function handleErrorResponse(error) {
  if (error?.response?.status === 401) {
    console.warn('401 response, showing login modal');
    store.dispatch(setSaving(false));
    store.dispatch(showLoginModal({ addToCart: false, reload: true }));
  } else {
    console.error('Error in response interceptor', error);
  }
  return Promise.reject(error);
}

export const appService = () => {
  const service = axios.create({
    baseURL: dotNetApiBase,
    withCredentials: true,
  });

  service.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      return handleErrorResponse(error);
    },
  );

  return service;
};

axiosRetry(appService(), {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 1000,
  retryCondition: () => true,
});

export function copyProject(newName, projectPK) {
  let url = 'userProject/copyProject';
  return app2Service().get(url, {
    params: { pk: projectPK, newName: newName },
  });
}

export function addProject(action) {
  let url = 'zoomUtils/createProject';
  return app2Service()
    .get(url, {
      params: {
        t: action.newProject.type,
        st: action.newProject.subtype,
        subOrgId: action.newProject.subOrgId,
        proj: action.newProject,
        redirect: false,
      },
    })
    .then((result) => {
      return result;
    });
}

export function spellCheckProject(projectPK) {
  let url = 'zoomUtils/spellCheckProject';
  return app2Service()
    .get(url, {
      params: {
        projectPK: projectPK,
      },
    })
    .then((result) => {
      return new Promise((resolve, reject) => {
        try {
          resolve(result.data);
        } catch (err) {
          reject(err);
        }
      });
    });
}

// Get project using either projectPK or shareID param
export function getProject(
  projectPKOrShareID,
  include = null,
  pages = null,
  section = null,
  spread = null,
  nodeID = null,
) {
  let url = 'userProject/getProjectAsJson';

  invariant(
    z
      .union([
        z.strictObject({ projectPK: z.union([z.string(), z.number()]) }),
        z.strictObject({ shareID: z.string() }),
      ])
      .safeParse(projectPKOrShareID).success,
    'Invalid projectPKOrShareID',
  );

  const id = { ...projectPKOrShareID };
  if (id.projectPK) {
    id.projectPK = deobfuscateProjectId(id.projectPK);
  }

  let params = {
    ...id,
    include: include,
    pages: pages,
    section: section,
    spread: spread,
    nodeID,
  };

  return app2Service()
    .get(url, {
      params,
    })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      console.error('Caught error when trying to load project', error);
      throw error;
    });
}

export function getPageForRenderer(projectPK, pageID, orderNumber) {
  let url = `userProject/getRendererPageJSON?projectPK=${projectPK}&pageID=${pageID}`;
  if (orderNumber != undefined) {
    url += `&orderNumber=${orderNumber}`;
  }
  return app2Service()
    .get(url)
    .then((result) => {
      return result.data;
    });
}

export function getProductInfo(projectPK) {
  let url = `userProject/getProductInfo?projectPK=${projectPK}`;
  return app2Service()
    .get(url)
    .then((result) => {
      return result.data;
    });
}

export function queueRendererJob(projectPK, useLogo) {
  let url = `userProject/queueRendererJob?projectPK=${projectPK}&logo=${useLogo.toString()}`;
  return app2Service()
    .get(url)
    .then((result) => {
      return result.data;
    });
}

export function validateCanUpload() {
  let url = 'userProject/validateCanUpload';
  return app2Service()
    .get(url)
    .then((result) => result.data.valid);
}

export function uploadToBlurby(jobPK) {
  let url = `userProject/upload?jobPK=${jobPK}`;
  return app2Service()
    .get(url)
    .then((result) => {
      return result.data;
    });
}

export function uploadToBlurbyStatus(jobPK) {
  let url = `userProject/uploadStatus?jobPK=${jobPK}`;
  return app2Service()
    .get(url)
    .then((result) => {
      return result.data;
    });
}

/**
 * Send a list of image ids and get the full metadata back.
 * @param projectPK
 * @param images Array of image ids
 */
export function getImagesMetadata(projectPK, images) {
  let url = 'userProject/getImagesMetadata';
  let params = {
    projectPK,
    images,
  };

  return app2Service()
    .post(url, params)
    .then((result) => {
      return result.data;
    });
}

export function getGalleries() {
  return app2Service()
    .get('gallery/')
    .then((result) => {
      return new Promise((resolve, reject) => {
        try {
          let galleries = result.data.map((item) => {
            return {
              pk: item.pk,
              name: item.name,
              creationTimestamp: new Date(item.creationTimestamp),
              thumbnail: item.thumbnail,
              imageCount: item.imageCount,
            };
          });

          resolve(galleries);
        } catch (err) {
          reject(err);
        }
      });
    });
}

export function getGalleryImages(pk) {
  return app2Service()
    .get('gallery/getImages', { params: { pk } })
    .then((result) => {
      return new Promise((resolve, reject) => {
        try {
          let galleries = result.data.map((item) => {
            return {
              imageID: item.id,
              thumbPath: item.thumbnail,
              highResPath: item.highResPath + '&ck=true',
              height: item.height,
              width: item.width,
              filename: item.filename,
              imageType: IMAGE_TYPE.NORMAL,
              orientation: 1,
              date: item.date,
            };
          });

          resolve(galleries);
        } catch (err) {
          reject(err);
        }
      });
    });
}

export function getPublishedProjects(token) {
  return app2Service()
    .get('userProject/getUserProjectsJSON', {
      params: {
        forLibrary: true,
      },
    })
    .then((result) => {
      return new Promise((resolve, reject) => {
        try {
          let projects = result.data.map((item) => {
            return {
              clientID: item.clientID,
              projectTitle: item.title,
              creationTimestamp: new Date(item.creationTimestamp),
              modificationTimestamp: new Date(item.modificationTimestamp),
              projectID: parseInt(item.pk),
              projectImages: parseInt(item.imageCount),
              projectPages: parseInt(item.pageCount),
              projectType: parseInt(item.projectType),
              projectSubType: parseInt(item.projectSubType),
              projectSubTypeName: item.projectSubTypeName,
              themePK: item.themePK,
              thumbnailURL: item.thumbnailURL,
              projectVersion: item.projectXMLVersion,
              renderJobs: item.renderJobs,
              shareID: item.shareID,
              product: { ...splitProduct(item.productPK), pk: item.productPK },
            };
          });

          resolve(projects);
        } catch (err) {
          reject(err);
        }
      });
    });
}

export function spellCheckStringService(string, ignoredWords = []) {
  let url = 'zoomUtils/spellCheckText';
  return app2Service()
    .get(url, {
      params: {
        text: string,
        ignoredWords: ignoredWords.toString(),
      },
    })
    .then((result) => {
      return result.data;
    });
}

export function saveDesignerLayout(type, productID, layout, id) {
  return app2Service().post('layouts/saveLayout', {
    type,
    productID,
    layout,
    id,
  });
}

export function deleteDesignerLayout(id) {
  return app2Service().get('layouts/deleteLayout', {
    params: { id },
  });
}

export function publish(projectPK) {
  let url = 'userProject/publish';
  return app2Service().get(url, {
    params: { projectPK },
  });
}

export async function logout() {
  try {
    z.strictObject({ result: z.literal(0) }).parse(
      (await app2Service().post('zoomUtils/logout')).data,
    );
  } catch (e) {
    console.error(e);
    throw Error('Failed to logout');
  }
}

export async function login(u, p, token) {
  return await app2Service()
    .post('zoomUtils/login', { u, p, token })
    .catch((e) => Promise.reject(e));
}

export async function register(u, p, email, token) {
  return await app2Service()
    .post('zoomUtils/register', { u, p, email, token })
    .catch((e) => Promise.reject(e));
}

function app2Get(url) {
  return app2Service().get(url);
}

export async function getEmptyWellsCount(projectPK) {
  let result = await app2Service().get('userProject/getEmptyWellCount', {
    params: { projectPK },
  });
  return result.data;
}
export async function addImageToGallery(images, galleryName) {
  return await app2Service().post('Gallery/addImages', {
    params: { images, galleryName },
  });
}

export async function renameGallery(pk, galleryName) {
  return await app2Service().post('Gallery/rename', {
    params: { pk, galleryName },
  });
}

export async function duplicateGallery(pk, galleryName) {
  return await app2Service().post('Gallery/duplicate', {
    params: { pk, galleryName },
  });
}

export async function mergeGalleries(intoPK, fromPK) {
  return await app2Service().post('Gallery/merge', { intoPK, fromPK });
}

export async function deleteGallery(pk) {
  return await app2Service().post('Gallery/delete', { params: { pk } });
}
export async function renameImage(imageID, name) {
  return await app2Service().post('Gallery/renameImage', {
    params: { imageID, name },
  });
}
export async function removeImageFromGallery(pk, imageID) {
  return await app2Service().post('Gallery/removeImage', { pk, imageID });
}

export async function createGallery(name) {
  return await app2Service().post('Gallery/create', { name });
}
